import React, { useEffect, useRef, useState } from 'react';
import { RecaptchaVerifier } from 'firebase/auth';
import { auth } from '../utils/firebase';
import { getFirebaseErrorMessage } from '../utils/errorHandler';

// Firebase enforces actual SMS quotas. This bounds accidental resends in one UI flow.
export default function SmsCodeForm({ sendCode, verifyCode, canSend = true, onCancel }) {
  const container = useRef(null);
  const verifier = useRef(null);
  const active = useRef(true);
  const inFlight = useRef(false);
  const [verificationId, setVerificationId] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(false);
  const cooldownTimer = useRef(null);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      verifier.current?.clear();
      verifier.current = null;
      clearTimeout(cooldownTimer.current);
    };
  }, []);

  const run = async (action) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try { await action(); }
    catch (err) {
      if (active.current) setError(getFirebaseErrorMessage(err, 'login'));
    } finally {
      inFlight.current = false;
      if (active.current) setBusy(false);
    }
  };

  const send = () => run(async () => {
    if (!canSend || cooldown || attempts >= 3) return;
    setAttempts(value => value + 1);
    setCooldown(true);
    cooldownTimer.current = setTimeout(() => {
      if (active.current) setCooldown(false);
    }, 60000);
    setVerificationId('');
    setCode('');
    // No NODE_ENV bypass: live SMS always goes through Firebase's verifier.
    const captcha = new RecaptchaVerifier(auth, container.current, { size: 'invisible' });
    verifier.current = captcha;
    try {
      const id = await sendCode(captcha);
      if (active.current) setVerificationId(id);
    } finally {
      if (verifier.current === captcha) {
        captcha.clear();
        verifier.current = null;
      }
    }
  });

  return (
    <div className="mfa-container">
      {error && <p role="alert" className="login-error-message">{error}</p>}
      <button type="button" className="center-button secondary" onClick={send}
        disabled={busy || !canSend || cooldown || attempts >= 3}>
        {attempts ? 'Resend code' : 'Send verification code'}
      </button>
      {cooldown && <p role="status">Wait one minute before requesting another code.</p>}
      {attempts >= 3 && <p>For another code, cancel and try again later.</p>}
      {verificationId && <>
        <label>SMS verification code
          <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            className="main-input" value={code} disabled={busy}
            onChange={event => setCode(event.target.value.replace(/\D/g, ''))} />
        </label>
        <button type="button" className="center-button primary" disabled={busy || !/^\d{6}$/.test(code)}
          onClick={() => run(() => verifyCode(verificationId, code))}>Verify code</button>
      </>}
      <button type="button" className="link-button" disabled={busy} onClick={onCancel}>Cancel</button>
      <div ref={container} />
    </div>
  );
}
